import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { Difficulty, MapId, Role } from '@il-colpo/engine';
import type { ConnectionState, LobbyConfig, LobbyPlayer } from '@il-colpo/protocol';
import { readAvatar } from '@il-colpo/shared/account';
import { AvatarFigure } from '../../account/AvatarFigure.tsx';
import { useGameStore } from '../../game/gameStore.ts';
import { useLooseT } from '../../game/text/loose.ts';
import { shareText } from '../../native/native.ts';
import { IN_CAPACITOR, inviteLink } from '../../native/shell.ts';
import { getConnection, useConnection } from '../../online/onlineConnection.ts';
import { OnlineGameClient } from '../../online/onlineGameClient.ts';
import { useToastStore } from '../../state/toastStore.ts';
import { useNow } from '../../systems/useNow.ts';
import { Badge, Button, Panel, ScreenFrame, SegmentedControl, Switch } from '../../ui/index.ts';
import styles from './Online.module.css';
import { PlayerActions } from '../social/PlayerActions.tsx';
import { RoomInvites } from '../social/RoomInvites.tsx';
import { ChatPanel, useIssueToasts } from './shared.tsx';

const MAPS: readonly MapId[] = ['bank', 'museum', 'villa', 'express'];
const DIFFICULTIES: readonly Difficulty[] = ['easy', 'normal', 'pro', 'legend'];
const INFILTRATOR: readonly LobbyConfig['infiltrator'][] = ['never', 'always', 'normal'];
const SIZES: readonly LobbyConfig['size'][] = [3, 4, 5];

/** Seconds left of a quick-match wait, counted down here between the server's messages. */
function useCountdown(state: ConnectionState): number | null {
  const now = useNow();
  const startsInMs = state.lobby?.startsInMs ?? null;
  if (startsInMs === null) return null;
  return Math.max(0, Math.ceil((startsInMs - (now - state.lobbyAt)) / 1000));
}

function Invite({ code }: { code: string }) {
  const { t } = useTranslation('online');
  const [qr, setQr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const link = inviteLink(code);

  useEffect(() => {
    let live = true;
    void import('qrcode')
      .then((m) => m.toDataURL(link, { margin: 1, width: 280 }))
      .then((url) => {
        if (live) setQr(url);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [link]);

  const copy = (text: string) => {
    void navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      })
      .catch(() => undefined);
  };

  return (
    <Panel title={t('room.code')}>
      <div className={styles['invite']}>
        <div className={styles['stack']}>
          <span className={styles['code']} data-testid="room-code">
            {code}
          </span>
          <div className={styles['row']}>
            <Button
              variant="secondary"
              onClick={() => {
                copy(code);
              }}
            >
              {t('room.copy')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                copy(link);
              }}
            >
              {t('room.copyLink')}
            </Button>
            {IN_CAPACITOR ? (
              <Button
                variant="secondary"
                onClick={() => {
                  void shareText({
                    title: t('room.shareTitle'),
                    text: t('room.shareText', { code }),
                    url: link,
                  });
                }}
              >
                {t('room.share')}
              </Button>
            ) : null}
          </div>
          {copied ? (
            <span className={styles['okNote']} role="status">
              {t('room.copied')}
            </span>
          ) : null}
        </div>
        {qr ? <img className={styles['qr']} src={qr} alt={t('room.qr')} /> : null}
      </div>
    </Panel>
  );
}

function PlayerRow({
  player,
  me,
  canKick,
  isPrivate,
}: {
  player: LobbyPlayer;
  me: boolean;
  canKick: boolean;
  isPrivate: boolean;
}) {
  const { t } = useTranslation('online');
  return (
    <li className={styles['player']} data-me={me} data-testid="room-player">
      <AvatarFigure avatar={readAvatar(player.avatar)} size="2.6rem" />
      <span className={styles['playerName']}>
        <strong>{player.name}</strong>
        <span className={styles['badges']}>
          {player.host && isPrivate ? <Badge tone="amber">{t('room.host')}</Badge> : null}
          {me ? <Badge tone="cyan">{t('room.you')}</Badge> : null}
          {isPrivate && !player.host ? (
            <Badge tone={player.ready ? 'green' : 'neutral'}>
              {player.ready ? t('room.ready') : t('room.notReady')}
            </Badge>
          ) : null}
          {!player.connected ? <Badge tone="pink">{t('room.dropped')}</Badge> : null}
        </span>
      </span>
      {!me ? <PlayerActions handle={player.name} /> : null}
      {canKick && !me ? (
        <Button
          variant="ghost"
          onClick={() => {
            getConnection().kick(player.id);
          }}
        >
          {t('room.kick')}
        </Button>
      ) : null}
    </li>
  );
}

function Rules({ lobby, host }: { lobby: NonNullable<ConnectionState['lobby']>; host: boolean }) {
  const { t } = useTranslation('online');
  const { t: ts } = useTranslation('setup');
  const g = useLooseT('game');
  const c = lobby.config;
  const change = (patch: Partial<LobbyConfig>) => {
    getConnection().patchConfig(patch);
  };

  if (!host) {
    return (
      <Panel title={t('room.rules')}>
        <div className={styles['stack']}>
          <dl className={styles['ruleList']}>
            <dt>{t('room.map')}</dt>
            <dd>{g(`maps.${c.mapId}.name`)}</dd>
            <dt>{t('room.difficulty')}</dt>
            <dd>{g(`difficulty.${c.difficulty}`)}</dd>
            <dt>{t('room.infiltrator')}</dt>
            <dd>{t(`room.infiltratorOptions.${c.infiltrator}`)}</dd>
            <dt>{t('room.size')}</dt>
            <dd>{t('room.sizeOption', { count: c.size })}</dd>
            <dt>{t('room.bots')}</dt>
            <dd>{c.bots ? t('room.botsOn') : t('room.botsOff')}</dd>
            <dt>{t('room.draft')}</dt>
            <dd>{c.draft ? t('room.draftOn') : t('room.draftOff')}</dd>
          </dl>
          <p className={styles['note']}>{t('room.rulesReadOnly')}</p>
        </div>
      </Panel>
    );
  }
  return (
    <Panel title={t('room.rules')}>
      <div className={styles['rules']}>
        <SegmentedControl
          label={t('room.map')}
          value={c.mapId}
          onChange={(mapId) => {
            change({ mapId });
          }}
          options={MAPS.map((m) => ({ value: m, label: g(`maps.${m}.name`) }))}
        />
        <SegmentedControl
          label={t('room.difficulty')}
          value={c.difficulty}
          onChange={(difficulty) => {
            change({ difficulty });
          }}
          options={DIFFICULTIES.map((d) => ({ value: d, label: g(`difficulty.${d}`) }))}
        />
        <SegmentedControl
          label={t('room.infiltrator')}
          value={c.infiltrator}
          onChange={(infiltrator) => {
            change({ infiltrator });
          }}
          options={INFILTRATOR.map((i) => ({ value: i, label: t(`room.infiltratorOptions.${i}`) }))}
        />
        <p className={styles['note']}>{ts(`infiltratorHint.${c.infiltrator}`)}</p>
        <SegmentedControl
          label={t('room.size')}
          value={c.size}
          onChange={(size) => {
            change({ size });
          }}
          options={SIZES.map((n) => ({ value: n, label: t('room.sizeOption', { count: n }) }))}
        />
        <Switch
          label={t('room.bots')}
          checked={c.bots}
          onChange={(bots) => {
            change({ bots });
          }}
        />
        <Switch
          label={t('room.draft')}
          checked={c.draft}
          onChange={(draft) => {
            change({ draft });
          }}
        />
        <p className={styles['note']}>{t('room.draftHint')}</p>
      </div>
    </Panel>
  );
}

/** The role draft: whose turn it is, what has been taken, and (on your turn) what you may take. */
function DraftPanel({
  draft,
  players,
  me,
  lobbyAt,
}: {
  draft: NonNullable<NonNullable<ConnectionState['lobby']>['draft']>;
  players: LobbyPlayer[];
  me: string | null;
  lobbyAt: number;
}) {
  const { t } = useTranslation('online');
  const g = useLooseT('game');
  const now = useNow();
  const seconds = Math.ceil(Math.max(0, draft.msLeft - (now - lobbyAt)) / 1000);
  const mine = draft.turn !== null && draft.turn === me;
  const nameOf = (seat: string) => players.find((p) => p.id === seat)?.name ?? seat;
  return (
    <Panel title={t('room.draftPanel.title')}>
      <div className={styles['stack']} data-testid="draft">
        <p className={styles['note']} role="status">
          {draft.turn === null
            ? t('room.draftPanel.starting')
            : mine
              ? t('room.draftPanel.yourTurn', { seconds })
              : t('room.draftPanel.turnOf', { name: nameOf(draft.turn), seconds })}
        </p>
        <h3 className={styles['subtitle']}>{t('room.draftPanel.order')}</h3>
        <ol className={styles['draftOrder']}>
          {draft.order.map((seat) => {
            const role = draft.picks[seat];
            return (
              <li key={seat} data-turn={seat === draft.turn} data-testid="draft-seat">
                <strong>{nameOf(seat)}</strong>
                <Badge tone={role ? 'green' : seat === draft.turn ? 'amber' : 'neutral'}>
                  {role
                    ? g(`roles.${role}`)
                    : seat === draft.turn
                      ? '…'
                      : t('room.draftPanel.waiting')}
                </Badge>
              </li>
            );
          })}
        </ol>
        {draft.free.length > 0 ? (
          <>
            <h3 className={styles['subtitle']}>{t('room.draftPanel.free')}</h3>
            <ul className={styles['draftRoles']}>
              {draft.free.map((role: Role) => (
                <li key={role}>
                  <div className={styles['stack']}>
                    <strong>{g(`roles.${role}`)}</strong>
                    <span className={styles['note']}>{g(`roleInfo.${role}.does`)}</span>
                  </div>
                  <Button
                    variant={mine ? 'primary' : 'secondary'}
                    disabled={!mine}
                    onClick={() => {
                      getConnection().pickRole(role);
                    }}
                  >
                    {t('room.draftPanel.pick', { role: g(`roles.${role}`) })}
                  </Button>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>
    </Panel>
  );
}

/** The room before the heist: who is here, the rules, a code to share, and the table's talk. */
export function RoomScreen() {
  const { t } = useTranslation('online');
  const state = useConnection();
  const navigate = useNavigate();
  const push = useToastStore((s) => s.push);
  const countdown = useCountdown(state);
  useIssueToasts();

  // The connection decides where the player belongs.
  const { status, view, kicked } = state;
  useEffect(() => {
    if (status === 'idle' || status === 'closed') {
      if (kicked) push(t('room.kicked'), 'info');
      void navigate('/online', { replace: true });
      return;
    }
    if ((status === 'playing' || status === 'over') && view) {
      const store = useGameStore.getState();
      if (!(store.client instanceof OnlineGameClient))
        store.attach(new OnlineGameClient(getConnection()));
      void navigate('/game', { replace: true });
    }
  }, [status, view, kicked, navigate, push, t]);

  const lobby = state.lobby;
  if (!lobby || !state.code) {
    return (
      <ScreenFrame title={t('room.title')} backTo="/online">
        <div className={styles['page']}>
          <p className={styles['note']} role="status">
            {t('hub.connecting')}
          </p>
        </div>
      </ScreenFrame>
    );
  }

  const isPrivate = state.mode === 'private';
  const isRanked = state.mode === 'ranked';
  const meSeat = state.you;
  const me = lobby.players.find((p) => p.id === meSeat);
  const host = me?.host === true;
  const others = lobby.players.filter((p) => !p.host);
  const seats = lobby.config.bots
    ? Math.max(lobby.config.size, lobby.players.length)
    : lobby.players.length;
  const everyoneReady = others.every((p) => p.ready);
  const canStart = seats >= 3 && everyoneReady;

  return (
    <ScreenFrame
      title={
        isPrivate ? t('room.privateInfo') : isRanked ? t('room.rankedInfo') : t('room.quickInfo')
      }
      backTo="/online"
    >
      <div className={styles['page']}>
        {isPrivate ? <Invite code={state.code} /> : null}
        {isPrivate ? <RoomInvites /> : null}
        {!isPrivate ? (
          <Panel>
            <p className={styles['note']} role="status">
              {isRanked
                ? lobby.needed
                  ? t('room.rankedNeeded', { count: lobby.needed, here: lobby.players.length })
                  : countdown === null || countdown === 0
                    ? t('room.quickWaitNow')
                    : t('room.rankedWait', { seconds: countdown })
                : countdown === null || countdown === 0
                  ? t('room.quickWaitNow')
                  : t('room.quickWait', { seconds: countdown })}
            </p>
            {isRanked ? <p className={styles['note']}>{t('room.rankedRules')}</p> : null}
          </Panel>
        ) : null}

        <Panel title={t('room.players')}>
          <ul className={styles['players']}>
            {lobby.players.map((p) => (
              <PlayerRow
                key={p.id}
                player={p}
                me={p.id === meSeat}
                canKick={host && isPrivate}
                isPrivate={isPrivate}
              />
            ))}
          </ul>
        </Panel>

        {lobby.draft ? (
          <DraftPanel
            draft={lobby.draft}
            players={lobby.players}
            me={meSeat}
            lobbyAt={state.lobbyAt}
          />
        ) : null}

        {isPrivate && !lobby.draft ? <Rules lobby={lobby} host={host} /> : null}

        {isPrivate && !lobby.draft ? (
          <Panel>
            <div className={styles['stack']}>
              {host ? (
                <>
                  <Button
                    size="lg"
                    icon="play"
                    disabled={!canStart}
                    onClick={() => {
                      getConnection().start();
                    }}
                    block
                  >
                    {t('room.start')}
                  </Button>
                  {!canStart ? (
                    <p className={styles['note']}>
                      {seats < 3 ? t('room.startNeedsPlayers') : t('room.startWaiting')}
                    </p>
                  ) : null}
                </>
              ) : (
                <Button
                  size="lg"
                  variant={me?.ready ? 'secondary' : 'primary'}
                  onClick={() => {
                    getConnection().setReady(!me?.ready);
                  }}
                  block
                >
                  {me?.ready ? t('room.imNotReady') : t('room.imReady')}
                </Button>
              )}
            </div>
          </Panel>
        ) : null}

        <ChatPanel title />

        <div className={styles['rowEnd']}>
          <Button
            variant="ghost"
            onClick={() => {
              void getConnection()
                .leave()
                .then(() => navigate('/online', { replace: true }));
            }}
          >
            {t('room.leave')}
          </Button>
        </div>
      </div>
    </ScreenFrame>
  );
}
